import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Grid,
  makeStyles,
  IconButton,
  CardMedia,
  Fab,
  Tooltip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import { createBugReport } from "store/slices/thunks";
import Helper from "app/components/modules/HelperModal";
import { Modal } from "app/components/common/Modal";
import { Button } from "app/components/common/Button";
import { TextField } from "app/components/common/TextField";
import { useFileManager, useForm } from "hooks";
import { CloudUploadRounded } from "@material-ui/icons";
import BugReportIcon from "@material-ui/icons/BugReport";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { toast } from "react-toastify";

const PriorityOptions = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  CRITICAL: "Critical",
};
function BugReportModal({ open, onClose, initialFiles = [] }) {
  const classes = useStyles();
  const initialFilesRef = useRef(null);
  const [isLoading, setLoading] = useState(false);
  const {
    FileManager,
    openFileManager,
    filesData,
    onRemoveFile,
    onMultipleUpload,
    onClearMultipleFile,
    addMultipleFiles,
  } = useFileManager();
  const { data, errors, handleChange, handleSubmit, initFormData } = useForm({
    initialValues: {
      title: "",
      priority: PriorityOptions.LOW,
      description: "",
    },
    validations: {
      title: {
        required: {
          value: true,
          message: "This field is required!",
        },
      },
      description: {
        required: {
          value: true,
          message: "This field is required!",
        },
      },
    },
    onSubmit: () => {
      handleBugReport();
    },
  });
  const dispatch = useDispatch();
  const systemInfo = useMemo(
    () => ({
      url: window.location.href,
      browser: navigator.userAgent,
    }),
    [open]
  );
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    return () => {
      initFormData();
      onClearMultipleFile();
    };
  }, []);

  useEffect(() => {
    if (!open || initialFilesRef.current === initialFiles) return;
    initialFilesRef.current = initialFiles;
    addMultipleFiles(initialFiles);
  }, [open, initialFiles]);

  /* -------------------------------------------------------------------------- */

  const handleBugReport = async () => {
    setLoading(true);
    let attachments = undefined;
    if (filesData.length) {
      let res = await onMultipleUpload(false);
      if (res.error) {
        setLoading(false);
        return;
      }
      attachments = res?.message.map((item) => item.id);
    }
    dispatch(
      createBugReport(
        {
          ...data,
          current_url: systemInfo.url,
          user_agent: systemInfo.browser,
          attachments,
        },
        handleBugReportCallback
      )
    );
  };

  const handleBugReportCallback = (error) => {
    if (!error) {
      toast.success("Bug report sent");
      handleClose();
    }
    setLoading(false);
  };

  const renderAttachments = useCallback(() => {
    if (!filesData.length) return;
    return (
      <div className={classes.totalCardMedia}>
        {filesData.map((fd, index) => {
          let { file, url } = fd;
          return (
            <Grid key={url}>
              <IconButton
                size={"small"}
                onClick={() => onRemoveFile(url)}
                className={classes.closePicture}
              >
                <HighlightOffIcon />
              </IconButton>
              <CardMedia
                className={classes.cardMedia}
                component="img"
                alt="Contemplative Reptile"
                image={url}
                title={file.name}
              />
            </Grid>
          );
        })}
      </div>
    );
  }, [filesData]);

  const handleClose = () => {
    initFormData();
    onClearMultipleFile();
    initialFilesRef.current = null;
    onClose();
  };

  // [bugDescription] = detailsBug;

  /** Cleans TextField input to preferable value */
  const textfieldSanitize = (e) => e.target.value;
  const radioSanitize = (e) => e.target.value;
  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={"Submit a Bug Report"}
      headerContent={<Helper helperKey={"bugReportModal"} />}
      maxWidth="md"
      content={
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                variant="outlined"
                error={errors.title}
                value={data.title}
                onChange={handleChange("title", textfieldSanitize)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Priority *</FormLabel>
                <RadioGroup
                  row
                  value={data.priority}
                  onChange={handleChange("priority", radioSanitize)}
                >
                  <FormControlLabel
                    value={"LOW"}
                    control={<Radio color="primary" />}
                    label={PriorityOptions.LOW}
                  />
                  <FormControlLabel
                    value={"MEDIUM"}
                    control={<Radio color="primary" />}
                    label={PriorityOptions.MEDIUM}
                  />
                  <FormControlLabel
                    value={"HIGH"}
                    control={<Radio color="primary" />}
                    label={PriorityOptions.HIGH}
                  />
                  <FormControlLabel
                    value={"CRITICAL"}
                    control={<Radio color="secondary" />}
                    label={PriorityOptions.CRITICAL}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                variant="outlined"
                error={errors.description}
                multiline
                rows={12}
                value={data.description}
                className={classes.multiline}
                onChange={handleChange("description", textfieldSanitize)}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography className={classes.sectionTitle}>
                Attachments
              </Typography>
              <FileManager multiple>
                <Button
                  className={classes.uploadButton}
                  type="button"
                  variant="contained"
                  color="primary"
                  maxWidth="50%"
                  onClick={() => openFileManager("image")}
                  startIcon={<CloudUploadRounded />}
                >
                  Upload Files
                </Button>
              </FileManager>
              <Grid container>{renderAttachments()}</Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography className={classes.sectionTitle}>
                Operating System
              </Typography>
              <div className={classes.systemInfo}>
                <Typography className={classes.sectionTitle} variant="caption">
                  Current URL
                </Typography>
                <Typography className={classes.systemInfoText}>
                  {systemInfo.url}
                </Typography>
                <Typography className={classes.sectionTitle} variant="caption">
                  Browser
                </Typography>
                <Typography className={classes.systemInfoText}>
                  {systemInfo.browser}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </form>
      }
      actions={
        <>
          <Button type="button" variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            style={{ marginRight: "2%", height: "33px" }}
            type="button"
            variant="contained"
            color="primary"
            maxWidth="50%"
            onClick={handleSubmit}
            loading={isLoading}
          >
            Submit Report
          </Button>
        </>
      }
    />
  );
}

const createScreenshotFile = async () => {
  const { default: html2canvas } = await import("html2canvas");

  return html2canvas(document.body, {
    useCORS: true,
    logging: false,
    ignoreElements: (element) =>
      element?.getAttribute?.("data-bug-report-ignore") === "true" ||
      element?.classList?.contains("MuiDialog-root"),
  }).then(
    (canvas) =>
      new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) return resolve(null);
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          resolve(
            new File([blob], `bug-report-screenshot-${timestamp}.png`, {
              type: "image/png",
            })
          );
        }, "image/png");
      })
  );
};

export const FloatingBugReportButton = () => {
  const classes = useStyles();
  const captureRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [initialFiles, setInitialFiles] = useState([]);

  const handleOpen = async () => {
    const captureId = Symbol("bug-report-screenshot");
    captureRef.current = captureId;
    setInitialFiles([]);
    setOpen(true);

    try {
      const screenshot = await createScreenshotFile();
      if (captureRef.current !== captureId) return;
      setInitialFiles(screenshot ? [screenshot] : []);
    } catch (error) {
      if (captureRef.current !== captureId) return;
      setInitialFiles([]);
      toast.error("Screenshot could not be attached");
    }
  };

  const handleClose = () => {
    captureRef.current = null;
    setOpen(false);
    setInitialFiles([]);
  };

  return (
    <div data-bug-report-ignore="true">
      <Tooltip title="Submit a bug report">
        <Fab
          color="secondary"
          className={classes.floatingButton}
          onClick={handleOpen}
          size="medium"
        >
          <BugReportIcon />
        </Fab>
      </Tooltip>
      <BugReportModal
        open={open}
        onClose={handleClose}
        initialFiles={initialFiles}
      />
    </div>
  );
};
/* -------------------------------------------------------------------------- */
const useStyles = makeStyles((theme) => ({
  multiline: {
    "& .MuiInputBase-root": {
      height: 160,
    },
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  totalCardMedia: {
    position: "relative",
    overflowX: "auto",
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    minHeight: "140px",
    padding: theme.spacing(0.5),
    width: "100%",
  },
  cardMedia: {
    maxHeight: "130px",
    marginLeft: theme.spacing(1),
    marginBlock: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    width: "auto",
    maxWidth: 250,
  },
  closePicture: {
    position: "absolute",
    top: 2,
    zIndex: 10,
    fill: `${theme.palette.common.white} !important`,
    background: theme.palette.common.white,
    "&:hover": {
      background: theme.palette.common.white,
    },
  },
  systemInfo: {
    border: `1px solid ${theme.palette.black["20"]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    minHeight: 140,
    backgroundColor: theme.palette.common.white,
  },
  systemInfoText: {
    fontSize: 12,
    wordBreak: "break-word",
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  floatingButton: {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 1200,
  },
}));

export default BugReportModal;
